/**
 * HTMX Navigation Handler
 *
 * Handles post-swap tasks for HTMX boosted sidebar navigation:
 * - Skeleton loading placeholder during request
 * - Page title update (navbar header stays persistent)
 * - jQuery plugin reinitialization
 * - Sidebar active state update
 * - Scroll reset
 */
(function () {
    'use strict';

    var alertTimer = null;
    var lastResponseDoc = null;

    // Skeleton placeholder shown while content loads.
    // Mimics a typical page: toolbar + stat cards + table card.
    // NOTE: This is innerHTML (not outerHTML) — the #page-content div must survive
    // so HTMX can still find its swap target.
    var SKELETON_INNER =
        '<div style="padding: 20px 16px;">' +

            // Toolbar area — heading + action button placeholder
            '<div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px;">' +
                '<div class="rf-skeleton rf-skeleton--heading" style="margin-bottom: 0;"></div>' +
                '<div class="rf-skeleton rf-skeleton--toolbar" style="margin-bottom: 0; width: 120px;"></div>' +
            '</div>' +

            // Stat cards row
            '<div style="display: flex; gap: 16px; margin-bottom: 20px;">' +
                '<div class="rf-skeleton rf-skeleton--card" style="flex: 1; margin-bottom: 0;">' +
                    '<div class="rf-skeleton rf-skeleton--text rf-skeleton--short" style="margin-bottom: 8px;"></div>' +
                    '<div class="rf-skeleton rf-skeleton--heading" style="width: 50%;"></div>' +
                '</div>' +
                '<div class="rf-skeleton rf-skeleton--card" style="flex: 1; margin-bottom: 0;">' +
                    '<div class="rf-skeleton rf-skeleton--text rf-skeleton--short" style="margin-bottom: 8px;"></div>' +
                    '<div class="rf-skeleton rf-skeleton--heading" style="width: 50%;"></div>' +
                '</div>' +
                '<div class="rf-skeleton rf-skeleton--card" style="flex: 1; margin-bottom: 0;">' +
                    '<div class="rf-skeleton rf-skeleton--text rf-skeleton--short" style="margin-bottom: 8px;"></div>' +
                    '<div class="rf-skeleton rf-skeleton--heading" style="width: 50%;"></div>' +
                '</div>' +
            '</div>' +

            // Main content card with table-like rows
            '<div class="rf-skeleton rf-skeleton--card">' +
                '<div class="rf-skeleton rf-skeleton--table-row"></div>' +
                '<div class="rf-skeleton rf-skeleton--table-row"></div>' +
                '<div class="rf-skeleton rf-skeleton--table-row"></div>' +
                '<div class="rf-skeleton rf-skeleton--table-row"></div>' +
                '<div class="rf-skeleton rf-skeleton--table-row"></div>' +
                '<div class="rf-skeleton rf-skeleton--table-row" style="border-bottom: none;"></div>' +
            '</div>' +

        '</div>';

    // --- Sidenav submenu skeleton ---
    // seeme's loadContent() leaves the panel empty while fetching. Inject an
    // rf-skeleton placeholder so the user sees a shimmer instead of blank space.
    var SIDENAV_SKELETON =
        '<div style="padding: 12px 16px;">' +
            '<div class="rf-skeleton rf-skeleton--text rf-skeleton--short" style="margin-bottom: 16px;"></div>' +
            '<div class="rf-skeleton rf-skeleton--text" style="height: 10px;"></div>' +
            '<div class="rf-skeleton rf-skeleton--text" style="height: 10px;"></div>' +
            '<div class="rf-skeleton rf-skeleton--text" style="height: 10px;"></div>' +
            '<div class="rf-skeleton rf-skeleton--text rf-skeleton--short" style="height: 10px;"></div>' +
        '</div>';

    document.body.addEventListener('htmx:beforeRequest', function (e) {
        var el = e.detail.elt;
        if (el && el.hasAttribute('data-sidenav-content')) {
            el.innerHTML = SIDENAV_SKELETON;
        }
    });

    // --- Restore boost attrs on panel body after sidenav submenu content loads ---
    // sidenav.js loadContent() temporarily overrides hx-target/swap/select/push-url
    // to do a one-time innerHTML load. After it settles, restore the original
    // boost attrs so child links navigate correctly via HTMX SPA.
    document.body.addEventListener('htmx:afterSettle', function (e) {
        var el = e.detail.elt;
        if (!el || !el.hasAttribute('data-sidenav-content')) return;
        // Only fix attrs that were overwritten by sidenav loadContent
        if (el.getAttribute('hx-target') === 'this') {
            el.setAttribute('hx-target', '#page-content');
            el.setAttribute('hx-select', '#page-content');
            el.setAttribute('hx-swap', 'outerHTML');
            el.setAttribute('hx-push-url', 'true');
            el.removeAttribute('hx-get');
            el.removeAttribute('hx-trigger');
            htmx.process(el);
        }
        // Mark active link in newly loaded panel content
        updateSidebarActive();
    });

    // --- Cancel boost for modal triggers / empty hrefs, then show skeleton ---
    document.body.addEventListener('htmx:beforeRequest', function (e) {
        if (!e.detail.boosted) return;

        // Cancel if it's a modal trigger or has no real href
        var elt = e.detail.elt;
        if (elt) {
            var link = elt.closest('a');
            if (link) {
                var href = link.getAttribute('href');
                if (link.classList.contains('show-modal') || link.hasAttribute('data-sheet') || !href || href === '#' || href === '') {
                    e.preventDefault();
                    return;
                }
            }
        }

        // Update URL immediately for sidenav links so address bar changes before slow response.
        // Uses replaceState (not pushState) so HTMX can still pushState + cache for back/forward.
        if (link && link.closest('[data-sidenav-content]')) {
            var pushHref = link.getAttribute('href');
            if (pushHref && pushHref !== '#' && pushHref !== '') {
                history.replaceState(null, '', pushHref);
            }
        }

        // Determine if this is a partial swap (e.g. inline filter) or full page swap.
        // Partial swaps target something other than #page-content — dim that element
        // instead of replacing the entire page with a skeleton.
        var targetEl = e.detail.requestConfig && e.detail.requestConfig.target
            ? (typeof e.detail.requestConfig.target === 'string'
                ? document.querySelector(e.detail.requestConfig.target)
                : e.detail.requestConfig.target)
            : null;
        var isPartialSwap = targetEl && targetEl.id && targetEl.id !== 'page-content';

        if (isPartialSwap) return;

        if (window.rfSheet && window.rfSheet.active()) {
            window.rfSheet.close();
        }

        // Full page swap — show skeleton, replace inner content but keep #page-content
        // so HTMX can still find its swap target
        var content = document.getElementById('page-content');
        if (content) content.innerHTML = SKELETON_INNER;

        // Show skeleton on the nav title too
        var navTitle = document.getElementById('nav-title');
        if (navTitle) {
            navTitle.innerHTML = '<span class="rf-skeleton rf-skeleton--text" style="width: 140px; height: 18px; display: inline-block; margin-bottom: 0; vertical-align: middle;"></span>';
        }
    });

    // --- Before swap: intercept redirects and non-three-column pages ---
    document.body.addEventListener('htmx:beforeSwap', function (e) {
        if (!e.detail.boosted) return;

        try {
            var xhr = e.detail.xhr;
            var responseText = xhr.responseText || '';

            // If redirected to login, do a full page redirect
            if (xhr.responseURL && xhr.responseURL.indexOf('/accounts/login') !== -1) {
                e.detail.shouldSwap = false;
                window.location.href = xhr.responseURL;
                return;
            }

            // If response has no #page-content (non-three-column page), do full navigation
            if (responseText && responseText.indexOf('id="page-content"') === -1) {
                e.detail.shouldSwap = false;
                var path = (e.detail.requestConfig && e.detail.requestConfig.path) || window.location.href;
                window.location.href = path;
                return;
            }

            // CSS track guard: legacy ↔ modern CSS track mismatch → full reload
            var pageContent = document.getElementById('page-content');
            var currentTrack = pageContent && pageContent.dataset.cssTrack;
            if (currentTrack && responseText) {
                var trackDiv = document.createElement('div');
                trackDiv.innerHTML = responseText;
                var incomingTrackEl = trackDiv.querySelector('[data-css-track]');
                var incomingTrack = incomingTrackEl && incomingTrackEl.dataset.cssTrack;
                if (incomingTrack && incomingTrack !== currentTrack) {
                    e.detail.shouldSwap = false;
                    var requestPath = (e.detail.requestConfig && e.detail.requestConfig.path) || window.location.href;
                    window.location.href = requestPath;
                    return;
                }
            }

            // Parse once — reused by both nav element updates and footer script execution
            var parser = new DOMParser();
            var doc = parser.parseFromString(responseText, 'text/html');
            lastResponseDoc = doc;

            updateNavElement('nav-title', doc);
            updateNavElement('nav-badge', doc);
            updateNavElement('nav-subtitle', doc);

            // Update browser tab title from response
            if (doc.title) document.title = doc.title;
        } catch (err) {
            console.error('[htmx-nav] beforeSwap error:', err);
        }
    });

    // --- After content swap ---
    document.body.addEventListener('htmx:afterSettle', function (e) {
        if (!e.detail.boosted) return;

        // Check if this is a partial swap (inline filter) vs full page swap.
        // After outerHTML swap the original target is detached — re-query by ID.
        var swapTarget = e.detail.target;
        var isPartialSwap = swapTarget && swapTarget.id && swapTarget.id !== 'page-content';
        var newSwapTarget = isPartialSwap ? document.getElementById(swapTarget.id) : null;

        // (Loading state for partial swaps handled by hx-indicator + rf-loading-overlay)

        // Scroll to top (skip for partial swaps — keep scroll position)
        var content = document.getElementById('page-content');
        if (!isPartialSwap) {
            if (content) content.scrollTop = 0;
            // Phones: the shell's main column is the scroller (mobile-nav locks the body)
            var shellMain = document.querySelector('.shell__main');
            if (shellMain) shellMain.scrollTop = 0;
            window.scrollTo(0, 0);
        }

        // Reprocess HTMX attributes in new content
        var swapRoot = newSwapTarget || content;
        if (swapRoot) htmx.process(swapRoot);

        // Reinitialize jQuery plugins on swapped content only
        reinitPlugins(newSwapTarget);

        if (isPartialSwap) {
            // Partial swap — update tab/nav links outside the swap target so their
            // hrefs carry the current query params (filters, sort, view, etc.)
            updateExternalLinkParams();
            lastResponseDoc = null;
            return;
        }

        // Re-execute page footer scripts from the response.
        // {% block footer_js %} lives outside #page-content (in base.html),
        // so hx-select strips it. We parse the full response, extract inline
        // scripts from #page-footer-scripts, and execute them here.
        executeFooterScripts(lastResponseDoc);
        ensureNgxScripts(lastResponseDoc);
        lastResponseDoc = null;

        // Update sidebar active state
        updateSidebarActive();
    });

    // --- Re-collapse an out-of-band swapped flexMenu strip ---
    // `reinitPlugins` above scopes to the PRIMARY swap target, so it never reaches a node brought
    // in by `hx-select-oob`. The rentals and deals tab strips are exactly that case: they sit
    // outside `#list-results-wrapper` and are OOB-swapped so their count badges track the filter.
    // flexMenu's overflow collapse ("More" + dropdown) exists only in the live DOM — the server
    // never renders it — so without this the strip comes back uncollapsed and wraps onto two rows.
    //
    // htmx fires `htmx:load` on OOB-swapped nodes (oobSwap defaults to outerHTML -> swapOuterHTML
    // -> insertNodesBefore -> makeAjaxLoadTask), and it bubbles, so one body-level listener covers
    // every such strip. `{undo: true}` first is flexMenu's own re-init idiom (see adjustFlexMenu in
    // vendor/flexmenu_new.js) and is what makes this safe to run on an already-collapsed menu.
    //
    // The modern `[data-tabs-collapse]` strips need none of this — seeme/tabs-collapse.js already
    // re-runs document-wide on afterSettle. This is the legacy flexMenu equivalent.
    document.body.addEventListener('htmx:load', function (e) {
        if (typeof $ === 'undefined') return;
        var node = e.detail && e.detail.elt;
        if (!node || node.nodeType !== 1) return;
        var $menus = $(node).find('[data-toggle="flexmenu"]').addBack('[data-toggle="flexmenu"]');
        if (!$menus.length) return;
        try {
            $menus.flexMenu({undo: true}).flexMenu({cutoff: '0', linkTitleAll: 'Boom'});
        } catch (err) {
            console.error('[htmx-nav] flexMenu re-init failed:', err);
        }
    });

    // --- bfcache guard: reload when we land back on a stuck skeleton ---
    // If a boosted click falls back to a full-page nav (cross-CSS-track, login
    // redirect, missing #page-content, network error), the browser can snapshot
    // the beforeRequest skeleton into bfcache. `pageshow` with e.persisted===true
    // fires on bfcache restore (popstate/load don't), so we reload from here.
    // Full trace + why not pagehide: .claude/docs/htmx-navigation.md §Back Button & bfcache
    window.addEventListener('pageshow', function (e) {
        if (!e.persisted) return;
        var pageContent = document.getElementById('page-content');
        if (pageContent && pageContent.querySelector('.rf-skeleton')) {
            window.location.reload();
        }
    });

    // --- Popstate fallback: reload when HTMX can't restore ---
    // When the user first loads a page (full page load), the history entry has no
    // HTMX state. If they SPA-navigate forward then press back, HTMX won't restore
    // because event.state has no htmx key. Fall back to a full reload.
    //
    // Registered at DOMContentLoaded, not here: window popstate listeners run in
    // registration order, and sheets.js (imported after this file) must run
    // first so Back from a sheet to the page under it can close the sheet and
    // stop the event before this reload.
    function onPopstateFallback(e) {
        if (!e.state || !e.state.htmx) {
            // If URL has ?sheet=, let sheets.js popstate handler open it
            var params = new URLSearchParams(window.location.search);
            if (params.has('sheet')) return;
            window.location.reload();
        }
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            window.addEventListener('popstate', onPopstateFallback);
        });
    } else {
        window.addEventListener('popstate', onPopstateFallback);
    }

    // --- On error, do full page navigation ---
    // Login redirects are handled natively by HTMX via HX-Redirect header
    // (set by HtmxAuthRedirectMiddleware). This only handles other errors.
    function handleNavError(e) {
        if (!e.detail.boosted) return;
        var url = e.detail.requestConfig && e.detail.requestConfig.path;
        if (url) window.location.href = url;
    }
    document.body.addEventListener('htmx:responseError', handleNavError);
    document.body.addEventListener('htmx:sendError', handleNavError);

    function updateNavElement(id, doc) {
        var newEl = doc.getElementById(id);
        var currentEl = document.getElementById(id);
        if (!currentEl) return;
        if (newEl) {
            currentEl.textContent = newEl.textContent;
            // an empty badge span (the page head always renders it) stays hidden
            currentEl.style.display = newEl.textContent.trim() ? '' : 'none';
        } else {
            currentEl.textContent = '';
            currentEl.style.display = 'none';
        }
    }

    function reinitPlugins(scopeEl) {
        if (typeof $ === 'undefined') return;

        // Scope to swapped content to avoid double-init on persistent elements
        var $scope = scopeEl ? $(scopeEl) : $('#page-content');
        if (!$scope.length) return;

        // Datepicker
        try { $scope.find('.datepicker').datepicker({weekStart: 0, dateFormat: 'yy-mm-dd'}); } catch (e) {}

        // Timepicker
        try { $scope.find('.timepicker').timepicker({'timeFormat': 'h:i A'}); } catch (e) {}

        // SelectPicker — destroy first to clean up portaled dropdowns (data-container:body)
        // that survive DOM swaps, then reinitialize fresh
        try {
            $scope.find('.selectpicker').each(function () {
                if ($.data(this, 'selectpicker')) $(this).selectpicker('destroy');
            });
            // Clean orphaned bs-container elements left by previous selectpickers
            // whose DOM was replaced by HTMX before destroy could run
            $('body > .bs-container').remove();
            $scope.find('.selectpicker').selectpicker();
        } catch (e) {}

        // Tooltip
        try { $scope.find('[data-toggle="tooltip"]').tooltip(); } catch (e) {}

        // Initials avatar
        try { $scope.find('.img-avatar').initial(); } catch (e) {}

        // Textarea autosize
        try { $scope.find('.form-control-autosize').textareaAutoSize(); } catch (e) {}

        // FlexMenu
        try { $scope.find('[data-toggle="flexmenu"]').flexMenu({cutoff: '0', linkTitleAll: 'Boom'}); } catch (e) {}

        // Truncatable widget
        try {
            $scope.find('.truncatable').each(function () {
                var parentWidth = Math.round($(this).parent().width());
                $(this).css('width', parentWidth + 'px');
                var item = $(this).find('.truncatable-item');
                if (item.length && item[0].scrollWidth <= item[0].clientWidth) {
                    $(this).removeClass('dropdown-toggle');
                } else {
                    $(this).addClass('dropdown-toggle');
                    $(this).css('cursor', 'pointer');
                }
            });
        } catch (e) {}

        // Dropdown init
        try { $scope.find('[data-toggle="dropdown"]').dropdown({boundary: 'viewport', reference: 'parent', flip: true}); } catch (e) {}

        // Combobox
        try { $scope.find('.combobox').combobox({bsVersion: '4'}); } catch (e) {}

        // Popover
        try {
            $scope.find('[data-toggle="popover"]').each(function () {
                $(this).popover({
                    html: true,
                    content: function () {
                        var id = $(this).attr('id');
                        return $('#popover-content-' + id).html();
                    }
                });
            });
        } catch (e) {}

        // Feedback alerts persist until the user dismisses them with the close (×) button —
        // they no longer auto-hide on a timer (was 4s; disappeared before people could read it).
        if (alertTimer) clearTimeout(alertTimer);
    }

    /**
     * After a partial swap (inline filter), update hrefs on links outside the
     * swap target (tabs, nav links) so they carry the current URL query params.
     */
    function updateExternalLinkParams() {
        var currentParams = window.location.search;
        // Update tab links — they have the base path baked in, just need current query
        var tabLinks = document.querySelectorAll('.nav-tabs a[href]');
        tabLinks.forEach(function (link) {
            var href = link.getAttribute('href');
            if (!href || href === '#' || href === '') return;
            // Replace everything after '?' (or append if no '?')
            var basePath = href.split('?')[0];
            link.setAttribute('href', basePath + currentParams);
        });
        // Also update .full-page-refresh links (sort, view, quick date, etc.)
        var refreshLinks = document.querySelectorAll('.full-page-refresh');
        refreshLinks.forEach(function (link) {
            var href = link.getAttribute('href');
            if (!href || href === '#' || href === '') return;
            var basePath = href.split('?')[0];
            link.setAttribute('href', basePath + currentParams);
        });
    }

    // Angular 12 custom elements (rt-*) are registered via customElements.define()
    // on initial page load. Modern pages (three-column-modern) don't load Angular
    // by default. If a modern page overrides {% block ngx %} to include Angular
    // (e.g. move-out detail), the scripts are in <head> — stripped by hx-select.
    // Extract them from the parsed response and inject if not already loaded.
    function ensureNgxScripts(doc) {
        if (!doc) return;
        if (document.querySelector('script[src*="/ngx/dist/"]')) return;

        var ngxScripts = doc.querySelectorAll('script[src*="/ngx/dist/"]');
        if (!ngxScripts.length) return;

        var ngxStyle = doc.querySelector('link[rel="stylesheet"][href*="/ngx/dist/"]');
        if (ngxStyle && !document.querySelector('link[href*="/ngx/dist/"]')) {
            var link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = ngxStyle.getAttribute('href');
            document.head.appendChild(link);
        }

        var srcs = [];
        ngxScripts.forEach(function (s) { srcs.push(s.getAttribute('src')); });

        function loadNext(i) {
            if (i >= srcs.length) return;
            var el = document.createElement('script');
            el.src = srcs[i];
            el.onload = function () { loadNext(i + 1); };
            el.onerror = function () {
                console.warn('[htmx-nav] Failed to load ngx script:', srcs[i]);
                loadNext(i + 1);
            };
            document.head.appendChild(el);
        }
        loadNext(0);
    }

    // Indirect (global-scope) eval. Aliasing `eval` to another name is what makes
    // it indirect — a direct `eval(...)` call would use the caller's local scope.
    var globalEval = eval;

    // Only #page-footer-scripts ({% block footer_js %}) is re-executed after a swap.
    // Per-page scripts in {% block body_js %} are NOT — that block renders outside
    // both the swap target and this container, so its scripts silently never run
    // on SPA navigation. Page-specific interactive JS belongs in footer_js.
    function executeFooterScripts(doc) {
        if (!doc) return;
        try {
            var container = doc.getElementById('page-footer-scripts');
            if (!container) return;

            container.querySelectorAll('script').forEach(function (script) {
                if (script.src) return; // skip external scripts — only inline
                try {
                    // Indirect eval — runs the body in GLOBAL scope, so a top-level
                    // `var` becomes a real global. Pages declare page config in one
                    // <script> ({% include %}d helpers then read it: `var filters`,
                    // ENTITY_NOTES_URL, recipient_email, …) and an IIFE wrapper made
                    // those function-local, so the helper threw "x is not defined" or
                    // silently reused the PREVIOUS page's value. Only broke on SPA
                    // nav; a hard refresh always worked.
                    //
                    // let/const/class from an indirect eval go into that eval's own
                    // declarative scope, NOT the global lexical environment, so
                    // re-executing across navigations still can't throw
                    // "Identifier has already been declared" — which is what the
                    // IIFE was there to prevent. Don't revert this to a wrapper.
                    globalEval(script.textContent + '\n//# sourceURL=footer-script.js');
                } catch (e) {
                    console.error('[htmx-nav] Footer script error:', e);
                }
            });
        } catch (err) {
            console.error('[htmx-nav] Footer script error:', err);
        }
    }

    function updateSidebarActive() {
        var sidenav = document.querySelector('[data-sidenav]');
        if (!sidenav) return;

        var path = window.location.pathname;
        var search = window.location.search;
        var fullUrl = path + search;

        // Clear active subnav links inside the panel
        sidenav.querySelectorAll('.nav-link.active').forEach(function (el) {
            el.classList.remove('active');
        });

        // Find matching link in the panel content
        var matched = false;
        var links = sidenav.querySelectorAll('[data-sidenav-content] a[href]');

        links.forEach(function (link) {
            var href = link.getAttribute('href');
            if (!href || href === '#') return;
            if (href === fullUrl || (href === path && !search)) {
                link.classList.add('active');
                matched = true;
            }
        });

        // Fallback: path prefix match on panel links
        if (!matched) {
            var bestMatch = null;
            var bestLen = 0;
            links.forEach(function (link) {
                var href = link.getAttribute('href');
                if (!href || href === '#' || href === '/') return;
                var candidates = [href.split('?')[0]];
                var extra = link.getAttribute('data-active-path');
                if (extra) {
                    extra.split(',').forEach(function (p) {
                        p = p.trim();
                        if (p) candidates.push(p);
                    });
                }
                for (var i = 0; i < candidates.length; i++) {
                    if (path.indexOf(candidates[i]) === 0 && candidates[i].length > bestLen) {
                        bestMatch = link;
                        bestLen = candidates[i].length;
                    }
                }
            });
            if (bestMatch) {
                bestMatch.classList.add('active');
            }
        }

    }

    /**
     * Activate the rail trigger whose data-sidenav-path prefix matches the current URL.
     * Supports comma-separated prefixes (e.g. "/direct/,/seller-proposals/").
     * Returns the matched trigger element, or null.
     */
    function activateTriggerByPath(sidenav) {
        var path = window.location.pathname;
        var triggers = sidenav.querySelectorAll('[data-sidenav-trigger][data-sidenav-path]');
        var bestTrigger = null;
        var bestLen = 0;

        triggers.forEach(function (trigger) {
            var prefixes = trigger.getAttribute('data-sidenav-path').split(',');
            for (var i = 0; i < prefixes.length; i++) {
                var prefix = prefixes[i].trim();
                if (prefix && path.indexOf(prefix) === 0 && prefix.length > bestLen) {
                    bestTrigger = trigger;
                    bestLen = prefix.length;
                }
            }
        });

        // Clear all active triggers, then set the match
        sidenav.querySelectorAll('[data-sidenav-trigger].nav__link--active').forEach(function (t) {
            t.classList.remove('nav__link--active');
        });
        if (bestTrigger) {
            bestTrigger.classList.add('nav__link--active');
        }
        return bestTrigger;
    }

    // Expose reinitPlugins so other modules (sheets.js) can reuse it
    window.rfReinitPlugins = reinitPlugins;

    // --- Auto-open correct section on page load ---
    document.addEventListener('DOMContentLoaded', function () {
        var sidenav = document.querySelector('[data-sidenav]');
        if (!sidenav) return;

        var trigger = activateTriggerByPath(sidenav);
        if (trigger && window.sidenav && window.sidenav.open) {
            var section = trigger.getAttribute('data-sidenav-trigger');
            if (section) window.sidenav.open(section, { silent: true });
        }
    });
})();
